import { useNotification } from '@app/AppLayout/NotificationContext';
import { CommunicationApi } from '@app/api';
import {
  COMMUNICATION_FIELD,
  Communication,
  CommunicationDraft,
  DEFAULT_ENABLED,
  DEFAULT_PUSH,
  RecipientSourceInfo,
  draftFrom,
  hasChanges,
  isFieldOverridden,
  sameRecipients,
  updateFrom,
  validateRecipients,
  validateTemplate
} from '@app/model/Communication';
import { useCommunicationStore } from '@app/store/communicationStore';
import { ApiError } from '@app/utils/ApiError';
import {
  Alert,
  AlertVariant,
  Button,
  Content,
  Divider,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Switch,
  TextArea,
  TextInput,
  Title,
  Tooltip
} from '@patternfly/react-core';
import { InfoCircleIcon, UndoIcon } from '@patternfly/react-icons';
import * as React from 'react';

import { RecipientListEditor } from './RecipientListEditor';

/**
 * What to tell the operator when a save fails.
 *
 * The awkward case is a rejected `PUT`: a plain BadRequestException is answered **without a body**
 * throughout this service, so the generic parse of nothing at all comes out as "An unknown error
 * occurred". Everything this form validates covers the 400s the endpoint can currently produce, so
 * reaching one means a rule that arrived after this was written — naming the four candidates beats
 * naming none of them.
 */
const reasonFor = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 403) {
      return 'Changing a communication needs the administrator role.';
    }
    if (error.code === 400) {
      return 'The service turned this change away. Check that every placeholder is one this '
        + 'communication passes, that the templates parse, and that every recipient names a source '
        + 'it still knows.';
    }
  }
  return error instanceof Error && error.message ? error.message : 'The communication could not be saved.';
};

export interface CommunicationEditModalProps {
  /** The communication to edit. Null closes the modal. */
  communication: Communication | null;
  sources: RecipientSourceInfo[];
  onClose(): void;
}

/**
 * The whole of one communication, in one form.
 *
 * One form rather than an editable table row because a `PUT` is a **full replacement**: a null
 * field means "no override" and puts that field back to what the code declares, so a screen that
 * wrote one field at a time would have to send the other eight back every time anyway. Loading and
 * saving the whole communication is what makes "reset just this field" expressible at all — it is
 * the Revert control on each field, which puts the value back to the default and therefore sends
 * null.
 *
 * Which is also why nothing here stores what is on screen verbatim. `updateFrom` sends null for
 * every field still equal to the catalog default, so an operator who turns email off writes one
 * column and the wording keeps tracking the code as it changes.
 */
export const CommunicationEditModal: React.FunctionComponent<CommunicationEditModalProps> = ({
  communication,
  sources,
  onClose
}) => {
  const { addNotification } = useNotification();
  const applyWrite = useCommunicationStore(state => state.applyWrite);

  const [draft, setDraft] = React.useState<CommunicationDraft | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);

  // Re-seeded whenever a different communication is opened, and whenever the one on screen is
  // replaced under the form — another admin's save arrives over the websocket, and a draft built
  // from wording that no longer exists would be saved straight over theirs.
  React.useEffect(() => {
    setDraft(communication ? draftFrom(communication) : null);
    setFailure(null);
  }, [communication]);

  if (!communication || !draft) return null;

  const set = <K extends keyof CommunicationDraft>(field: K, value: CommunicationDraft[K]) =>
    setDraft(current => (current ? { ...current, [field]: value } : current));

  const { placeholders, templatable } = communication;

  const subjectError = validateTemplate(draft.subjectTemplate, placeholders);
  const messageError = templatable ? validateTemplate(draft.pushMessageTemplate, placeholders) : null;
  const emailBodyError = templatable ? validateTemplate(draft.emailBodyTemplate, placeholders) : null;
  const toError = validateRecipients(draft.to, sources);
  const ccError = validateRecipients(draft.cc, sources);
  const bccError = validateRecipients(draft.bcc, sources);

  const isValid = !subjectError && !messageError && !emailBodyError && !toError && !ccError && !bccError;
  const isDirty = hasChanges(communication, draft);

  const save = async () => {
    if (!isValid || !isDirty) return;

    setIsSaving(true);
    setFailure(null);
    try {
      const saved = await CommunicationApi.set(communication.name, updateFrom(communication, draft));
      applyWrite(saved);
      addNotification(
        'Communication updated',
        AlertVariant.success,
        `${communication.name} is saved. It takes effect immediately, and within a minute on every `
          + 'other replica.'
      );
      onClose();
    } catch (error) {
      setFailure(reasonFor(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      variant={ModalVariant.large}
      isOpen
      onClose={onClose}
      aria-label={`Edit ${communication.name}`}
    >
      <ModalHeader title={communication.name} description={communication.description} />
      <ModalBody>
        {failure && (
          <Alert
            variant="danger"
            isInline
            title={failure}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          />
        )}

        <Form onSubmit={event => { event.preventDefault(); void save(); }}>
          <Title headingLevel="h3" size="md">Delivery</Title>

          <FormGroup fieldId="communication-enabled">
            <Switch
              id="communication-enabled"
              label="Raise this communication"
              isChecked={draft.enabled}
              onChange={(_event, checked) => set('enabled', checked)}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={draft.enabled ? 'indeterminate' : 'warning'}>
                  {draft.enabled
                    ? 'The gate, and the only one. Every producer in the service goes through it.'
                    : 'Off: nothing is written to anybody’s inbox, pushed or emailed. Producers that '
                      + 'wait on the result are told which communication to turn back on.'}
                </HelperTextItem>
                <OverrideNote
                  communication={communication}
                  field={COMMUNICATION_FIELD.ENABLED}
                  isDefault={draft.enabled === DEFAULT_ENABLED}
                  describeDefault={String(DEFAULT_ENABLED)}
                  onRevert={() => set('enabled', DEFAULT_ENABLED)}
                />
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup fieldId="communication-push">
            <Switch
              id="communication-push"
              label="Push it to the browser"
              isChecked={draft.push}
              isDisabled={!draft.enabled}
              onChange={(_event, checked) => set('push', checked)}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant="indeterminate">
                  Whether it arrives as a toast while the recipient is signed in. It never removes
                  the inbox row — that is what the email’s deep link points back at, so it is
                  written either way.
                </HelperTextItem>
                <OverrideNote
                  communication={communication}
                  field={COMMUNICATION_FIELD.PUSH}
                  isDefault={draft.push === DEFAULT_PUSH}
                  describeDefault={String(DEFAULT_PUSH)}
                  onRevert={() => set('push', DEFAULT_PUSH)}
                />
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup fieldId="communication-email">
            <Switch
              id="communication-email"
              label="Send it by email"
              isChecked={draft.email}
              isDisabled={!draft.enabled}
              onChange={(_event, checked) => set('email', checked)}
            />
            <FormHelperText>
              <HelperText>
                {/* The master switch is a service-wide setting rather than anything about this
                    communication, so turning email on here while it is off changes nothing — and
                    an operator who is not told that will believe they fixed the problem. */}
                {!communication.emailMasterSwitch && draft.email ? (
                  <HelperTextItem variant="warning">
                    Email is turned off for the whole service, so nothing is mailed whatever this
                    says. The master switch is the notification email setting under Administration
                    &gt; General Settings.
                  </HelperTextItem>
                ) : (
                  <HelperTextItem variant="indeterminate">
                    Mail goes to the recipients below, which are not the same people as the audience
                    that can see it in their inbox.
                  </HelperTextItem>
                )}
                <OverrideNote
                  communication={communication}
                  field={COMMUNICATION_FIELD.EMAIL}
                  isDefault={draft.email === communication.defaultEmail}
                  describeDefault={String(communication.defaultEmail)}
                  onRevert={() => set('email', communication.defaultEmail)}
                />
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <Divider />
          <Title headingLevel="h3" size="md">Wording</Title>

          {!templatable && (
            <Alert
              variant="info"
              isInline
              isPlain
              title="This communication’s producers write the whole message"
            >
              Its callers describe genuinely different situations and each supplies its own wording,
              so there is no template to edit and the service refuses one. The subject, the delivery
              switches and the recipients are still yours.
            </Alert>
          )}

          <TemplateField
            id="communication-subject"
            label="Subject"
            help="The subject line of the mail, and the title in the inbox."
            value={draft.subjectTemplate}
            defaultValue={communication.defaultSubjectTemplate}
            placeholders={placeholders}
            error={subjectError}
            communication={communication}
            field={COMMUNICATION_FIELD.SUBJECT_TEMPLATE}
            onChange={value => set('subjectTemplate', value)}
          />

          {templatable && (
            <>
              <TemplateField
                id="communication-message"
                label="Message"
                help="What the inbox row and the toast say. Plain text — it is not HTML, and is not escaped."
                rows={3}
                value={draft.pushMessageTemplate}
                defaultValue={communication.defaultPushMessageTemplate}
                placeholders={placeholders}
                error={messageError}
                communication={communication}
                field={COMMUNICATION_FIELD.PUSH_MESSAGE_TEMPLATE}
                onChange={value => set('pushMessageTemplate', value)}
              />

              <TemplateField
                id="communication-email-body"
                label="Email body"
                help="HTML, and deliberately so: the markup is yours and is left alone, while the
                      values substituted into it are escaped."
                rows={6}
                isMonospace
                value={draft.emailBodyTemplate}
                defaultValue={communication.defaultEmailBodyTemplate}
                placeholders={placeholders}
                error={emailBodyError}
                communication={communication}
                field={COMMUNICATION_FIELD.EMAIL_BODY_TEMPLATE}
                onChange={value => set('emailBodyTemplate', value)}
              />
            </>
          )}

          <Divider />
          <Title headingLevel="h3" size="md">Recipients</Title>
          <Content component="p" style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
            Who the <b>mail</b> goes to. Not the same question as who can see the notification —
            that audience is {audienceReads(communication)}, and a facility contact or an external
            instructor has no account and no inbox.
          </Content>

          {!draft.email && (
            <Alert variant="info" isInline isPlain title="Email is off for this communication">
              These recipients are kept and change nothing until it is turned back on.
            </Alert>
          )}

          <RecipientField
            id="communication-to"
            field="TO"
            label="To"
            draftValue={draft.to}
            defaultValue={communication.defaultTo}
            sources={sources}
            error={toError}
            communication={communication}
            fieldKey={COMMUNICATION_FIELD.TO}
            onChange={value => set('to', value)}
          />
          <RecipientField
            id="communication-cc"
            field="CC"
            label="Cc"
            draftValue={draft.cc}
            defaultValue={communication.defaultCc}
            sources={sources}
            error={ccError}
            communication={communication}
            fieldKey={COMMUNICATION_FIELD.CC}
            onChange={value => set('cc', value)}
          />
          <RecipientField
            id="communication-bcc"
            field="BCC"
            label="Bcc"
            draftValue={draft.bcc}
            defaultValue={communication.defaultBcc}
            sources={sources}
            error={bccError}
            communication={communication}
            fieldKey={COMMUNICATION_FIELD.BCC}
            onChange={value => set('bcc', value)}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          key="save"
          variant="primary"
          isDisabled={!isValid || !isDirty || isSaving}
          isLoading={isSaving}
          onClick={() => void save()}
        >
          Save
        </Button>
        <Button key="cancel" variant="link" isDisabled={isSaving} onClick={onClose}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
};

/** How the audience reads in a sentence. Read only: who may see a notification is the producer's
 * decision, not an operator's, which is why the view reports it and takes no override for it. */
const audienceReads = (communication: Communication): string => {
  switch (communication.defaultAudienceScope) {
    case 'ROLE':
      return `everybody with the ${communication.defaultAudienceValue} role`;
    case 'ALL':
      return 'every signed-in user';
    default:
      return 'whoever the producer names';
  }
};

/**
 * Says which layer is answering for a field, and offers to hand it back to the code.
 *
 * There is no per-field endpoint to call. Reverting sets the value back to what the catalog
 * declares, which is what makes `updateFrom` send null for it — so "reset this one field" is
 * expressed entirely in the draft, and is only really saved when the form is.
 */
const OverrideNote: React.FunctionComponent<{
  communication: Communication;
  field: COMMUNICATION_FIELD;
  isDefault: boolean;
  describeDefault: string;
  onRevert(): void;
}> = ({ communication, field, isDefault, describeDefault, onRevert }) => {
  // Nothing to say: the field matches the catalog and the table is not answering for it either.
  if (isDefault && !isFieldOverridden(communication, field)) return null;

  return (
    <HelperTextItem variant="indeterminate">
      <Flex spaceItems={{ default: 'spaceItemsXs' }} alignItems={{ default: 'alignItemsCenter' }}>
        <FlexItem>
          <Label isCompact color={isDefault ? 'grey' : 'blue'} icon={<InfoCircleIcon />}>
            {isDefault ? 'Back to the default when saved' : 'Changed from the default'}
          </Label>
        </FlexItem>
        {!isDefault && (
          <FlexItem>
            <Tooltip content={`The code declares ${describeDefault || 'nothing'}`}>
              <Button variant="link" isInline icon={<UndoIcon />} onClick={onRevert}>
                Revert
              </Button>
            </Tooltip>
          </FlexItem>
        )}
      </Flex>
    </HelperTextItem>
  );
};

/** One template box, with the placeholders it may use offered as chips that insert at the cursor. */
const TemplateField: React.FunctionComponent<{
  id: string;
  label: string;
  help: string;
  rows?: number;
  isMonospace?: boolean;
  value: string;
  defaultValue: string | null;
  placeholders: string[];
  error: string | null;
  communication: Communication;
  field: COMMUNICATION_FIELD;
  onChange(value: string): void;
}> = ({
  id, label, help, rows, isMonospace = false, value, defaultValue, placeholders, error,
  communication, field, onChange
}) => {
  const inputRef = React.useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  /**
   * Inserts a placeholder where the caret is, rather than appending it.
   *
   * The whole reason the chips are worth having over a list to copy from: a subject line is
   * usually edited in the middle. Falls back to appending if the box was never focused, which is
   * what `selectionStart` being null means.
   */
  const insert = (placeholder: string) => {
    const input = inputRef.current;
    const token = `{${placeholder}}`;
    const at = input?.selectionStart ?? value.length;
    const to = input?.selectionEnd ?? at;
    onChange(value.slice(0, at) + token + value.slice(to));

    // After React has rewritten the value, put the caret after what was just inserted so a second
    // chip does not land back at the start.
    window.requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  const monospace = isMonospace ? { fontFamily: 'var(--pf-t--global--font--family--mono)' } : undefined;

  return (
    <FormGroup label={label} fieldId={id}>
      {rows ? (
        <TextArea
          id={id}
          ref={inputRef}
          aria-label={label}
          value={value}
          rows={rows}
          resizeOrientation="vertical"
          validated={error ? 'error' : 'default'}
          style={monospace}
          onChange={(_event, next) => onChange(next)}
        />
      ) : (
        <TextInput
          id={id}
          ref={inputRef}
          aria-label={label}
          value={value}
          validated={error ? 'error' : 'default'}
          style={monospace}
          onChange={(_event, next) => onChange(next)}
        />
      )}

      {placeholders.length > 0 && (
        <Flex
          spaceItems={{ default: 'spaceItemsXs' }}
          alignItems={{ default: 'alignItemsCenter' }}
          style={{ marginTop: 'var(--pf-t--global--spacer--xs)' }}
        >
          <FlexItem>
            <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>Insert:</span>
          </FlexItem>
          {placeholders.map(placeholder => (
            <FlexItem key={placeholder}>
              <Button
                variant="plain"
                isInline
                aria-label={`Insert ${placeholder} into ${label}`}
                onClick={() => insert(placeholder)}
              >
                <Label isCompact color="purple">{`{${placeholder}}`}</Label>
              </Button>
            </FlexItem>
          ))}
        </Flex>
      )}

      <FormHelperText>
        <HelperText>
          <HelperTextItem variant={error ? 'error' : 'indeterminate'}>{error ?? help}</HelperTextItem>
          <OverrideNote
            communication={communication}
            field={field}
            isDefault={(value.trim() || null) === (defaultValue?.trim() || null)}
            describeDefault={defaultValue ?? ''}
            onRevert={() => onChange(defaultValue ?? '')}
          />
        </HelperText>
      </FormHelperText>
    </FormGroup>
  );
};

/** One recipient line, wrapped in the label, validation and revert the template boxes get. */
const RecipientField: React.FunctionComponent<{
  id: string;
  field: 'TO' | 'CC' | 'BCC';
  label: string;
  draftValue: string | null;
  defaultValue: string | null;
  sources: RecipientSourceInfo[];
  error: string | null;
  communication: Communication;
  fieldKey: COMMUNICATION_FIELD;
  onChange(value: string | null): void;
}> = ({ id, field, label, draftValue, defaultValue, sources, error, communication, fieldKey, onChange }) => (
  <FormGroup label={label} fieldId={id}>
    <RecipientListEditor
      fieldId={id}
      field={field}
      value={draftValue}
      defaultValue={defaultValue}
      sources={sources}
      onChange={onChange}
    />
    <FormHelperText>
      <HelperText>
        {error && <HelperTextItem variant="error">{error}</HelperTextItem>}
        <OverrideNote
          communication={communication}
          field={fieldKey}
          isDefault={sameRecipients(draftValue, defaultValue)}
          describeDefault={defaultValue ?? 'nobody'}
          onRevert={() => onChange(defaultValue)}
        />
      </HelperText>
    </FormHelperText>
  </FormGroup>
);
